import { useState } from "react";
import { Paper, IconButton } from "@mui/material";
import ModeIcon from "@mui/icons-material/Mode";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  AutocompleteModel,
  AutocompleteModelForAddress,
  AutocompleteModelForFirmaAchizitie,
  AutocompleteModelForTipAchizitie,
  AutocompleteModelForTipTVA,
  GenericAutocompleteModalModel,
} from "../../Models/Models";
import {
  AutocompleteSchema,
  AutocompleteSchemaForAddress,
  AutocompleteSchemaForFirmaAchizitie,
  AutocompleteSchemaForTipTVA,
} from "../../Validators/vehicleValidators";
import { isMobile } from "../../Utils/utils";
import { urlEnum } from "../../Utils/urlEnum";
import { uploadEntityLogo } from "../../Utils/logoUploadHelper";
import { createVehicleMainInfoColumns } from "./columns";

interface UseAutocompleteModalOptions {
  brandId: any;
}

const initialGenericModalState = new GenericAutocompleteModalModel();

// Adapted from CreateVehicle/GeneralInformation/useAutocompleteModal.tsx, but the
// switch bodies below intentionally preserve VehicleMainInfo's own current behavior
// (static hiddenFieldsArray, no addOnly mode) rather than that sibling's per-field
// logic, which differs.
export const useAutocompleteModal = ({ brandId }: UseAutocompleteModalOptions) => {
  const [genericAutocompleteModel, setGenericAutocompleteModel] = useState<any>(
    initialGenericModalState,
  );
  const [openModalUser, setOpenModalUser] = useState(false);
  const [openModalCompany, setOpenModalCompany] = useState(false);

  const handleBrandLogo = async (file: File, brandData: any) => {
    const response: any = await uploadEntityLogo(file, brandData, urlEnum.brand_r);
    setGenericAutocompleteModel((prev: any) => ({
      ...prev,
      data: response?.data?.brands,
      columns: columnsBrand,
      open: true,
    }));
  };

  const handleLocationLogo = async (file: File, locationData: any) => {
    const response: any = await uploadEntityLogo(
      file,
      locationData,
      urlEnum.locatie_administrativa_r,
    );
    setGenericAutocompleteModel((prev: any) => ({
      ...prev,
      data: response?.data?.locations,
      columns: columnsLocatieAdministrativa,
      open: true,
    }));
  };

  const {
    columnsLocatieAdministrativa,
    columns,
    columnsFirmaAchizitie,
    columnsTipTVA,
    columnsBrand,
    purchaseTypeColumns,
  } = createVehicleMainInfoColumns({
    onUploadLocationLogo: handleLocationLogo,
    onUploadBrandLogo: handleBrandLogo,
    genericAutocompleteModelData: genericAutocompleteModel.data,
  });

  const getAutocompleteValidationSchema = (id: string) => {
    switch (id) {
      case "locatie_administrativa_r":
        return AutocompleteSchemaForAddress;
      case "firma_achizitie_r":
        return AutocompleteSchemaForFirmaAchizitie;
      case "tip_tva_r":
        return AutocompleteSchemaForTipTVA;
      default:
        return AutocompleteSchema;
    }
  };

  const getAutocompleteColumns = (id: string) => {
    switch (id) {
      case "locatie_administrativa_r":
        return columnsLocatieAdministrativa;
      case "firma_achizitie_r":
        return columnsFirmaAchizitie;
      case "tip_tva_r":
        return columnsTipTVA;
      case "brand_r":
        return columnsBrand;
      case "tip_achizitie_r":
        return purchaseTypeColumns;
      default:
        return columns;
    }
  };

  const getAutocompleteModel = (id: string) => {
    switch (id) {
      case "locatie_administrativa_r":
        return new AutocompleteModelForAddress();
      case "model_r":
        return brandId;
      case "firma_achizitie_r":
        return new AutocompleteModelForFirmaAchizitie();
      case "tip_tva_r":
        return new AutocompleteModelForTipTVA();
      case "tip_achizitie_r":
        return new AutocompleteModelForTipAchizitie();
      default:
        return new AutocompleteModel();
    }
  };

  const handleClickEditDrop = (data: any, title: string, id: string) => {
    setGenericAutocompleteModel({
      ...genericAutocompleteModel,
      open: true,
      title: title,
      data: data,
      id: id,
      columns: getAutocompleteColumns(id),
      validationSchema: getAutocompleteValidationSchema(id),
      hiddenFieldsArray: ["brand_id", "id", "logo"],
      modelForFields: getAutocompleteModel(id),
    });
  };

  const DropDownButton = ({ children, ...other }: any) => (
    <Paper {...other}>
      <IconButton
        onMouseDown={(event: any) => {
          event.preventDefault();
          children[1] === "null" || children[1] === null
            ? handleClickEditDrop(
                children[2]?.props?.ownerState?.options,
                children[2]?.props?.ownerState?.label,
                children[2].props?.ownerState?.id,
              )
            : handleClickEditDrop(
                children[1]?.props?.ownerState?.options,
                children[1]?.props?.ownerState?.label,
                children[1].props?.ownerState?.id,
              );
        }}
        style={{
          position: "absolute",
          top: -40,
          right: isMobile() ? 0 : -36,
          margin: 5,
          display: "flex",
          justifyContent: "end",
          backgroundColor: "#FF6633",
          color: "#fff",
          borderRadius: 7,
          zIndex: 100,
        }}
        size="small"
        aria-label="editDropdown"
      >
        <ModeIcon fontSize="inherit" />
      </IconButton>
      {children}
    </Paper>
  );

  function EditButton({ children, ...other }: any) {
    return (
      <Paper {...other}>
        <IconButton
          onMouseDown={(event: any) => {
            event.preventDefault();
            setOpenModalUser(true);
          }}
          style={{
            position: "absolute",
            top: -40,
            right: -36,
            margin: 5,
            display: "flex",
            justifyContent: "end",
            backgroundColor: "#FF6633",
            color: "#fff",
            borderRadius: 7,
            zIndex: 100,
          }}
          size="small"
          aria-label="editDropdown"
        >
          <AddCircleIcon fontSize="inherit" />
        </IconButton>
        {children}
      </Paper>
    );
  }

  function EditButtonCompany({ children, ...other }: any) {
    return (
      <Paper {...other}>
        <IconButton
          onMouseDown={(event: any) => {
            event.preventDefault();
            setOpenModalCompany(true);
          }}
          style={{
            position: "absolute",
            top: -40,
            right: -36,
            margin: 5,
            display: "flex",
            justifyContent: "end",
            backgroundColor: "#FF6633",
            color: "#fff",
            borderRadius: 7,
            zIndex: 100,
          }}
          size="small"
          aria-label="editDropdown"
        >
          <AddCircleIcon fontSize="inherit" />
        </IconButton>
        {children}
      </Paper>
    );
  }

  return {
    genericAutocompleteModel,
    setGenericAutocompleteModel,
    handleClickEditDrop,
    handleBrandLogo,
    handleLocationLogo,
    DropDownButton,
    EditButton,
    EditButtonCompany,
    openModalUser,
    setOpenModalUser,
    openModalCompany,
    setOpenModalCompany,
  };
};

export default useAutocompleteModal;
